// Client-side PDF export of the resilience report. Generated entirely in the
// browser (nothing is sent to the server), so it is privacy-safe.
import { jsPDF } from "jspdf";
import { formatCompact, formatMonths, formatPercent, runwayFull, formatDateID } from "@/lib/currency";
import { resilienceHeadline } from "@/lib/calculations";

const STATUS = {
  HIJAU: { label: "HIJAU — Kuat", rgb: [5, 150, 105] },
  KUNING: { label: "KUNING — Cukup", rgb: [217, 119, 6] },
  MERAH: { label: "MERAH — Rapuh", rgb: [225, 29, 72] },
};

export function exportReportPdf(report, recs = [], opts = {}) {
  const free = !!opts.free;
  const { base, hero, sideStress, data } = report;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  let y = 56;

  const st = STATUS[base.status] || STATUS.MERAH;

  const line = (x1, yy, x2) => {
    doc.setDrawColor(228, 228, 231);
    doc.line(x1, yy, x2, yy);
  };
  const ensure = (need) => {
    if (y + need > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage();
      y = 56;
    }
  };
  const label = (txt) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(113, 113, 122);
    doc.text(txt.toUpperCase(), M, y);
    y += 16;
  };
  const row = (k, v, color) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(82, 82, 91);
    doc.text(k, M, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...(color || [24, 24, 27]));
    doc.text(String(v), W - M, y, { align: "right" });
    y += 18;
  };

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(24, 24, 27);
  doc.text("CeKKeU", M, y);
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(113, 113, 122);
  doc.text(free ? "Ringkasan Ketahanan Keluarga" : "Laporan Ketahanan Keluarga", M, y);
  doc.setFontSize(9);
  doc.text(formatDateID(report.createdAt || new Date().toISOString()), W - M, y, { align: "right" });
  y += 14;
  line(M, y, W - M);
  y += 26;

  // Score + status
  doc.setFont("helvetica", "bold");
  doc.setFontSize(42);
  doc.setTextColor(...st.rgb);
  doc.text(String(base.score), M, y + 10);
  doc.setFontSize(12);
  doc.setTextColor(113, 113, 122);
  doc.text("/ 100", M + 58, y + 10);
  doc.setFontSize(13);
  doc.setTextColor(...st.rgb);
  doc.text(st.label, W - M, y - 6, { align: "right" });
  y += 30;

  // Headline
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(82, 82, 91);
  const head = report.headline || resilienceHeadline(base) || "";
  if (head) {
    const lines = doc.splitTextToSize(head, W - M * 2);
    doc.text(lines, M, y);
    y += lines.length * 14 + 8;
  }
  line(M, y, W - M);
  y += 24;

  // Component breakdown
  label("Rincian Skor (V2.1)");
  const b = base.scoreBreakdown || {};
  row("Dana Darurat", `${b.emergency ?? 0} / 30`);
  row("Beban Cicilan", `${b.debt ?? 0} / 25`);
  row("Bertahan Pasca-Guncangan", `${b.shock ?? 0} / 30`);
  row("Kerentanan Penghasilan Tambahan", `${b.sideVulnerability ?? 0} / 15`);
  y += 8;
  line(M, y, W - M);
  y += 24;

  // Key metrics
  label("Indikator Kunci");
  row("Dana Darurat", `${formatMonths(base.emergencyMonths)} bulan`);
  row("Cicilan / Gaji Utama", `${formatPercent(base.debtToCoreIncome)}%`);
  row("Ketergantungan Penghasilan Tambahan", `${formatPercent(base.sideDependency)}%`);
  row(
    "Surplus Bulanan",
    `${base.monthlyBalance >= 0 ? "" : "-"}${formatCompact(Math.abs(base.monthlyBalance))}`,
    base.monthlyBalance >= 0 ? [5, 150, 105] : [225, 29, 72]
  );
  y += 8;
  line(M, y, W - M);
  y += 24;

  // Shock scenarios
  ensure(120);
  label("Uji Ketahanan");
  const heroLbl = hero.target === "husband" ? "Jika gaji suami berhenti" : "Jika gaji istri berhenti";
  row(
    heroLbl,
    hero.after.isPositive ? "Cashflow tetap positif" : `Bertahan ${runwayFull(hero.after.survivalRunway)}`,
    hero.after.isPositive ? [5, 150, 105] : [24, 24, 27]
  );
  if (sideStress) {
    row(
      "Jika penghasilan tambahan hilang total",
      `Skor ${sideStress.score} · ${sideStress.status}`,
      STATUS[sideStress.status]?.rgb
    );
  }
  y += 8;
  line(M, y, W - M);
  y += 24;

  // Recommendations (full report) or upgrade CTA (free summary)
  if (!free && recs && recs.length) {
    label("Prioritas Penguatan");
    recs.slice(0, 3).forEach((r, i) => {
      ensure(70);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(24, 24, 27);
      doc.text(`${i + 1}. ${r.title}`, M, y);
      y += 15;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(82, 82, 91);
      const tgt = doc.splitTextToSize(`Target: ${r.target}`, W - M * 2);
      doc.text(tgt, M, y);
      y += tgt.length * 12;
      const act = doc.splitTextToSize(`Langkah: ${r.action}`, W - M * 2);
      doc.setTextColor(5, 150, 105);
      doc.text(act, M, y);
      y += act.length * 12 + 10;
    });
  }

  if (free) {
    ensure(96);
    doc.setFillColor(24, 24, 27);
    doc.roundedRect(M, y, W - 2 * M, 76, 8, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text("Buka Laporan Lengkap", M + 16, y + 24);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(212, 212, 216);
    const t = doc.splitTextToSize(
      "Dapatkan rekomendasi prioritas, target dana darurat, simulasi interaktif, dan stress test penghasilan tambahan 0–100% di cekkeuangan.com.",
      W - 2 * M - 32
    );
    doc.text(t, M + 16, y + 40);
    y += 88;
  }

  // Footer disclaimer on every page
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    const H = doc.internal.pageSize.getHeight();
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(161, 161, 170);
    doc.text(
      "Estimasi edukatif berbasis data yang Anda masukkan. Bukan pengganti nasihat keuangan profesional.",
      M,
      H - 36,
      { maxWidth: W - M * 2 }
    );
    doc.text(`Halaman ${p}/${pages}`, W - M, H - 36, { align: "right" });
  }

  const fname = free ? "Ringkasan" : "Laporan";
  doc.save(`${fname}-Ketahanan-Keluarga-${new Date().toISOString().slice(0, 10)}.pdf`);
}
